/**
 * Navbar - Top bar with notifications, theme toggle, user menu
 */
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, IconButton, Avatar, Box, Typography, Menu, MenuItem, Badge, Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { supabase } from '../../lib/supabase';

const Navbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const { on, off } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;

    // Initial load
    const loadUnread = async () => {
      try {
        const res = await notificationAPI.getByUser(user.id);
        if (res.data?.success && res.data?.data?.notifications) {
          const unread = (res.data.data.notifications || []).filter((n) => !n.is_read).length;
          setUnreadCount(unread);
        }
      } catch { /* ignore */ }
    };

    loadUnread();

    // ── Realtime subscription — badge updates instantly on new notification ──
    const channel = supabase
      .channel(`navbar-notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // New notification arrived — increment badge immediately
          if (!payload.new?.is_read) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Notification marked as read — reload count
          loadUnread();
        }
      )
      .subscribe();

    // Socket fallback
    const onNewNotification = () => loadUnread();
    on('notification', onNewNotification);

    return () => {
      supabase.removeChannel(channel);
      off('notification', onNewNotification);
    };
  }, [user?.id, on, off]);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ gap: { xs: 0.5, sm: 1 }, minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
        <IconButton edge="start" onClick={onMenuClick} color="inherit" size="small">
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'}>
          <IconButton onClick={toggleTheme} color="inherit" size="small">
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications">
          <IconButton color="inherit" size="small" onClick={() => navigate('/notifications')}>
            <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error" max={99}>
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Box
          component="button"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            cursor: 'pointer', 
            ml: 0.5,
            bgcolor: 'transparent',
            border: 'none',
            color: 'inherit',
            p: 0,
            fontFamily: 'inherit',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              borderRadius: 1
            }
          }}
        >
          <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
            <Typography variant="body2" fontWeight={600} noWrap>{user?.name || 'User'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {user?.role?.replace('_', ' ') || 'Resident'}
            </Typography>
          </Box>
          <Avatar sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, bgcolor: 'primary.main', fontSize: 14 }}>
            {user?.name?.charAt(0)}
          </Avatar>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>Settings</MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
